import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Mail, Send, Users, MapPin, Loader2, CheckCircle2, FileSpreadsheet, ListPlus, AlertTriangle } from 'lucide-react';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Standard email regex (RFC-5322 lite — good enough for UI validation)
const EMAIL_RE = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;

export default function BulkEmailManager({ token }) {
  const [users, setUsers] = useState([]);
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);

  const [targetType, setTargetType] = useState('all'); // 'all' | 'city' | 'custom'
  const [selectedCity, setSelectedCity] = useState('');
  const [customEmailsText, setCustomEmailsText] = useState('');

  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');

  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const uRes = await axios.get(`${API_URL}/api/admin/users`, { headers: { Authorization: `Bearer ${token}` } });
        const cRes = await axios.get(`${API_URL}/api/cities`);
        setUsers(Array.isArray(uRes.data) ? uRes.data : []);
        setCities(Array.isArray(cRes.data) ? cRes.data : []);
      } catch (e) {
        toast.error('Failed to load users/cities');
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  // ---------- Recipient derivation ----------
  const validUsers = useMemo(
    () => users.filter(u => u.email && EMAIL_RE.test(String(u.email).trim())),
    [users]
  );

  // Parse custom list — split by comma / newline / semicolon / whitespace
  const customParsed = useMemo(() => {
    if (targetType !== 'custom') return { valid: [], invalid: [], duplicates: 0, total: 0 };
    const raw = customEmailsText
      .split(/[,\s;\n]+/)
      .map(e => e.trim())
      .filter(Boolean);
    const total = raw.length;
    const seen = new Set();
    const valid = [];
    const invalid = [];
    let duplicates = 0;
    for (const email of raw) {
      const norm = email.toLowerCase();
      if (seen.has(norm)) { duplicates += 1; continue; }
      seen.add(norm);
      if (EMAIL_RE.test(email)) valid.push(email);
      else invalid.push(email);
    }
    return { valid, invalid, duplicates, total };
  }, [customEmailsText, targetType]);

  const targetEmails = useMemo(() => {
    if (targetType === 'all') return validUsers.map(u => u.email);
    if (targetType === 'city') {
      return validUsers
        .filter(u => (u.city || '').toLowerCase() === selectedCity.toLowerCase())
        .map(u => u.email);
    }
    return customParsed.valid;
  }, [targetType, validUsers, selectedCity, customParsed]);

  const recipientRows = useMemo(() => {
    if (targetType === 'all') {
      return validUsers.map(u => ({ email: u.email, name: u.name || '', city: u.city || '', mobile: u.phone || '' }));
    }
    if (targetType === 'city') {
      return validUsers
        .filter(u => (u.city || '').toLowerCase() === selectedCity.toLowerCase())
        .map(u => ({ email: u.email, name: u.name || '', city: u.city || '', mobile: u.phone || '' }));
    }
    return customParsed.valid.map(e => ({ email: e, name: '', city: '', mobile: '' }));
  }, [targetType, validUsers, selectedCity, customParsed]);

  const customHasInvalid = targetType === 'custom' && customParsed.invalid.length > 0;
  const canSend =
    !!subject.trim() &&
    targetEmails.length > 0 &&
    !customHasInvalid;

  // ---------- Handlers ----------
  const handleSend = async () => {
    if (!subject.trim()) return toast.error('Subject is required');
    if (!body || body === '<p><br></p>') return toast.error('Email body is required');
    if (targetEmails.length === 0) return toast.error('No valid email recipients selected');
    if (customHasInvalid) return toast.error(`${customParsed.invalid.length} invalid email(s) — please fix or remove them`);

    if (!window.confirm(`Send email to ${targetEmails.length} recipient(s)? This runs in the background.`)) return;

    setSending(true);
    setSendResult(null);
    try {
      const payload = { subject, body, recipient_emails: targetEmails };
      const res = await axios.post(`${API_URL}/api/admin/bulk-emails`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(res.data.message || 'Bulk email job queued successfully');
      setSendResult(res.data);
      setSubject('');
      setBody('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to dispatch emails');
    }
    setSending(false);
  };

  const handleExportRecipients = async () => {
    if (recipientRows.length === 0) return toast.error('No recipients to export');
    try {
      const XLSX = await import('xlsx');
      const ws = XLSX.utils.json_to_sheet(recipientRows.map((r, i) => ({
        'Sr No': i + 1,
        Email: r.email,
        Name: r.name,
        City: r.city,
        Mobile: r.mobile,
      })));
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Recipients');
      const suffix = targetType === 'city' ? `_${selectedCity || 'city'}` : (targetType === 'custom' ? '_Custom' : '_All');
      XLSX.writeFile(wb, `Kagajbook_BulkEmail_Recipients${suffix}.xlsx`);
      toast.success('Recipient list exported');
    } catch (err) {
      console.error(err);
      toast.error('Failed to export recipients');
    }
  };

  const handleDedupeCustom = () => {
    if (customParsed.total === 0) return;
    // Rebuild textarea from the deduped + normalized set (valid + invalid preserved)
    const seen = new Set();
    const out = [];
    for (const email of customEmailsText.split(/[,\s;\n]+/).map(e => e.trim()).filter(Boolean)) {
      const norm = email.toLowerCase();
      if (seen.has(norm)) continue;
      seen.add(norm);
      out.push(email);
    }
    setCustomEmailsText(out.join(', '));
    toast.success(`Removed ${customParsed.duplicates} duplicate(s)`);
  };

  const modules = {
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'list': 'ordered' }, { 'list': 'bullet' }],
      ['link'],
      ['clean']
    ]
  };

  if (loading) return <div className="p-8 text-center text-[#64748B]">Loading Data...</div>;

  const recipientBadge = targetType === 'custom' ? customParsed.valid.length : targetEmails.length;

  return (
    <div className="space-y-6" data-testid="bulk-email-tab">

      {/* Header Card */}
      <div className="bg-gradient-to-br from-[#172554] to-[#0f1a3a] rounded-xl shadow-sm p-6 text-white flex items-center gap-4">
        <div className="w-14 h-14 rounded-xl bg-[#F5B800] text-[#172554] flex items-center justify-center shrink-0">
          <Mail className="w-7 h-7" />
        </div>
        <div className="flex-1">
          <h2 className="text-xl font-extrabold flex items-center gap-2">
            Bulk Email Marketing
            <span className="text-[10px] font-bold uppercase tracking-wider bg-white/20 px-2 py-0.5 rounded-full">Background Job</span>
          </h2>
          <p className="text-white/70 text-sm mt-1 text-gujarati">
            મોટી સંખ્યામાં યુઝર્સને એકસાથે ઈમેલ મોકલો.
          </p>
        </div>
        <button
          onClick={handleExportRecipients}
          disabled={recipientRows.length === 0}
          className="hidden md:flex items-center gap-2 bg-white/10 hover:bg-white/20 disabled:opacity-40 disabled:cursor-not-allowed text-white text-xs font-bold px-4 py-2.5 rounded-lg transition-colors border border-white/20"
          data-testid="bulk-email-export-recipients"
        >
          <FileSpreadsheet className="w-4 h-4" /> Export Recipient List
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

        {/* Left Column: Target Selection */}
        <div className="space-y-6">
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
            <h3 className="font-bold text-[#172554] mb-4 flex items-center gap-2">
              <Users className="w-4 h-4 text-[#F5B800]" /> Select Recipients
            </h3>

            <div className="space-y-3">
              <label className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${targetType === 'all' ? 'bg-blue-50 border-[#172554] ring-1 ring-[#172554]' : 'hover:bg-gray-50 border-gray-200'}`}>
                <input type="radio" name="target" checked={targetType === 'all'} onChange={() => setTargetType('all')} className="w-4 h-4 text-[#172554]" data-testid="target-all-radio" />
                <div className="flex-1">
                  <div className="font-bold text-[#172554] text-sm">All Registered Users</div>
                  <div className="text-xs text-[#64748B] text-gujarati">તમામ નોંધાયેલા યુઝર્સ</div>
                </div>
                <span className="text-xs font-extrabold text-[#172554]">{validUsers.length}</span>
              </label>

              <label className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${targetType === 'city' ? 'bg-blue-50 border-[#172554] ring-1 ring-[#172554]' : 'hover:bg-gray-50 border-gray-200'}`}>
                <input type="radio" name="target" checked={targetType === 'city'} onChange={() => setTargetType('city')} className="w-4 h-4 text-[#172554]" data-testid="target-city-radio" />
                <div className="flex-1">
                  <div className="font-bold text-[#172554] text-sm">Filter by City</div>
                  <div className="text-xs text-[#64748B] text-gujarati">શહેર મુજબ પસંદ કરો</div>
                </div>
              </label>

              <label className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${targetType === 'custom' ? 'bg-blue-50 border-[#172554] ring-1 ring-[#172554]' : 'hover:bg-gray-50 border-gray-200'}`}>
                <input type="radio" name="target" checked={targetType === 'custom'} onChange={() => setTargetType('custom')} className="w-4 h-4 text-[#172554]" data-testid="target-custom-radio" />
                <div className="flex-1">
                  <div className="font-bold text-[#172554] text-sm flex items-center gap-1.5">
                    <ListPlus className="w-3.5 h-3.5" /> Enter Custom Emails
                  </div>
                  <div className="text-xs text-[#64748B] text-gujarati">કસ્ટમ ઈમેલ યાદી પેસ્ટ કરો</div>
                </div>
              </label>
            </div>

            {targetType === 'city' && (
              <div className="mt-4 pl-8">
                <select
                  value={selectedCity}
                  onChange={e => setSelectedCity(e.target.value)}
                  className="w-full border-gray-300 rounded text-sm text-[#172554] focus:ring-[#F5B800] focus:border-[#F5B800]"
                  data-testid="target-city-select"
                >
                  <option value="">Select a city...</option>
                  {cities.map(c => (
                    <option key={c.id} value={c.name}>{c.name}</option>
                  ))}
                </select>
              </div>
            )}

            {targetType === 'custom' && (
              <div className="mt-4 space-y-2">
                <label className="block text-xs font-bold text-[#172554]">Paste emails (comma / newline separated)</label>
                <textarea
                  value={customEmailsText}
                  onChange={e => setCustomEmailsText(e.target.value)}
                  placeholder="user1@example.com, user2@example.com&#10;user3@example.com"
                  rows={5}
                  className="w-full border border-gray-300 rounded p-2.5 text-xs font-mono outline-none focus:border-[#172554] resize-y"
                  data-testid="custom-emails-textarea"
                />
                <div className="flex flex-wrap items-center gap-2 text-[11px]">
                  <span className="px-2 py-1 rounded-full bg-green-50 text-green-700 font-bold border border-green-200" data-testid="custom-valid-count">
                    ✓ {customParsed.valid.length} Valid
                  </span>
                  {customParsed.invalid.length > 0 && (
                    <span className="px-2 py-1 rounded-full bg-red-50 text-red-700 font-bold border border-red-200" data-testid="custom-invalid-count">
                      ✗ {customParsed.invalid.length} Invalid
                    </span>
                  )}
                  {customParsed.duplicates > 0 && (
                    <span className="px-2 py-1 rounded-full bg-yellow-50 text-yellow-800 font-bold border border-yellow-200" data-testid="custom-duplicate-count">
                      ⇔ {customParsed.duplicates} Duplicates skipped
                    </span>
                  )}
                  {customParsed.duplicates > 0 && (
                    <button
                      onClick={handleDedupeCustom}
                      className="ml-auto text-[11px] font-bold text-[#172554] hover:text-[#F5B800] underline underline-offset-2"
                      data-testid="custom-dedupe-btn"
                    >
                      Remove duplicates
                    </button>
                  )}
                </div>
                {customParsed.invalid.length > 0 && (
                  <div className="mt-2 p-2 rounded-lg bg-red-50 border border-red-200 text-[11px] text-red-700 flex items-start gap-2" data-testid="custom-invalid-list">
                    <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                    <div className="break-all">
                      <div className="font-bold mb-1">Invalid emails (fix or remove to enable Send):</div>
                      {customParsed.invalid.slice(0, 8).join(', ')}
                      {customParsed.invalid.length > 8 && ` … +${customParsed.invalid.length - 8} more`}
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="mt-6 pt-4 border-t border-gray-100 flex items-center justify-between">
              <span className="text-xs font-bold text-[#64748B] uppercase tracking-wider">Valid Emails</span>
              <span className="text-2xl font-extrabold text-[#172554]" data-testid="bulk-email-valid-count">{recipientBadge}</span>
            </div>

            <button
              onClick={handleExportRecipients}
              disabled={recipientRows.length === 0}
              className="mt-3 w-full md:hidden flex items-center justify-center gap-2 bg-[#F8FAFC] hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed text-[#172554] text-xs font-bold px-4 py-2 rounded-lg border border-gray-200"
              data-testid="bulk-email-export-recipients-mobile"
            >
              <FileSpreadsheet className="w-4 h-4" /> Export Recipient List
            </button>

            {targetType === 'city' && selectedCity && targetEmails.length === 0 && (
              <p className="text-xs text-red-500 mt-2 text-center font-medium">No valid emails found in {selectedCity}</p>
            )}
          </div>

          {sendResult && (
            <div className="bg-green-50 border border-green-200 rounded-xl p-5" data-testid="bulk-email-result">
              <h4 className="font-bold text-green-800 text-sm mb-2 flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4" /> Job Dispatched
              </h4>
              <p className="text-xs text-green-700">
                The email has been queued to send to {sendResult.queued_count} recipients in the background.
              </p>
            </div>
          )}
        </div>

        {/* Right Column: Editor */}
        <div className="lg:col-span-2 bg-white rounded-xl shadow-sm border border-gray-100 p-5 flex flex-col h-full">
          <h3 className="font-bold text-[#172554] mb-4 flex items-center gap-2">
            <Mail className="w-4 h-4 text-[#F5B800]" /> Compose Email (ઈમેલ લખો)
          </h3>

          <div className="space-y-4 flex-1 flex flex-col">
            <div>
              <label className="block text-xs font-bold text-[#172554] mb-1">Subject (વિષય) *</label>
              <input
                type="text"
                value={subject}
                onChange={e => setSubject(e.target.value)}
                placeholder="e.g. નવી સુવિધા હવે કાગજબુક પર ઉપલબ્ધ છે!"
                className="w-full border border-gray-300 rounded p-2.5 text-sm outline-none focus:border-[#172554] text-gujarati font-medium"
                data-testid="bulk-email-subject"
              />
            </div>

            <div className="flex-1 min-h-[300px] flex flex-col">
              <label className="block text-xs font-bold text-[#172554] mb-1">Message Body (મુખ્ય લખાણ) *</label>
              <div className="flex-1 bg-white" data-testid="bulk-email-body">
                <ReactQuill
                  theme="snow"
                  value={body}
                  onChange={setBody}
                  modules={modules}
                  className="h-[250px] mb-12"
                  placeholder="Type your email content here. You can use bold, lists, and links."
                />
              </div>
            </div>
          </div>

          <div className="mt-4 pt-4 border-t border-gray-100 flex justify-end">
            <button
              onClick={handleSend}
              disabled={sending || !canSend}
              title={customHasInvalid ? 'Fix or remove invalid custom emails to enable Send' : ''}
              className="bg-[#172554] text-white font-bold px-6 py-2.5 rounded-lg hover:bg-[#0f1a3a] disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 transition-colors shadow-sm"
              data-testid="bulk-email-send-btn"
            >
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              {sending ? 'Dispatching...' : `Send to ${targetEmails.length} Users`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
